import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Space, Typography, Tag, Button, Descriptions, Tooltip, Alert } from 'antd';
import { normalizeBase } from '../utils';

const { Title, Text } = Typography;

type HealthJson = {
  status?: string;
  info?: unknown;
  error?: unknown;
  details?: {
    db?: { status?: string; latencyMs?: number };
    redis?: {
      status?: string;
      latencyMs?: number;
      queue?: { name?: string; wait?: number; active?: number; delayed?: number };
    };
    env?: {
      status?: string;
      details?: {
        missingRequired?: string[];
        invalidRequired?: string[];
        presentRequired?: string[];
        presentOptional?: string[];
      };
    };
  };
};

function latencyTag(ms?: number) {
  if (typeof ms !== 'number') return <Tag>n/a</Tag>;
  if (ms < 50) return <Tag color="green">{ms} ms</Tag>;
  if (ms < 150) return <Tag color="gold">{ms} ms</Tag>;
  return <Tag color="red">{ms} ms</Tag>;
}

export default function SystemDetailsPanel({ apiBase }: { apiBase: string }) {
  const [data, setData] = useState<HealthJson | null>(null);
  const [rawText, setRawText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setError(null);
    setData(null);
    setRawText('');
    if (apiBase == null) return;
    try {
      setLoading(true);
      const base = normalizeBase(apiBase);
      const url = `${base}/api/health/ready`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const text = await res.text();
      setRawText(text);
      try {
        setData(JSON.parse(text) as HealthJson);
      } catch {
        setData(null);
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      setError((e as Error)?.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    void run();
  }, [run]);

  const envSummary = useMemo(() => {
    const env = data?.details?.env?.details;
    return {
      missing: env?.missingRequired?.length ?? 0,
      invalid: env?.invalidRequired?.length ?? 0,
      required: env?.presentRequired?.length ?? 0,
      optional: env?.presentOptional?.length ?? 0,
      names: {
        missing: env?.missingRequired ?? [],
        invalid: env?.invalidRequired ?? [],
      },
    };
  }, [data]);

  const copyJson = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(rawText || '');
    } catch {
      // ignore
    }
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            System Details (Ready)
          </Title>
          {data?.status ? (
            <Tag color={data.status === 'ok' ? 'green' : 'red'}>{data.status}</Tag>
          ) : null}
        </Space>
      }
      extra={
        <Space>
          <Button onClick={() => run()} loading={loading}>
            Refresh
          </Button>
          {apiBase != null ? (
            <Button
              href={`${normalizeBase(apiBase)}/api/health/ready`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open
            </Button>
          ) : null}
          <Button onClick={() => void copyJson()}>Copy JSON</Button>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {apiBase == null && <Text type="secondary">API base not configured.</Text>}
      {error && <Alert type="warning" title={error} style={{ marginBottom: 8 }} />}

      <Space orientation="vertical" style={{ width: '100%' }}>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Database">
            <Space>
              <Text>status:</Text>
              <Tag color={data?.details?.db?.status === 'up' ? 'green' : 'red'}>
                {data?.details?.db?.status ?? 'n/a'}
              </Tag>
              <Text>latency:</Text>
              {latencyTag(data?.details?.db?.latencyMs)}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Redis">
            <Space size="small" wrap>
              <Text>status:</Text>
              <Tag color={data?.details?.redis?.status === 'up' ? 'green' : 'red'}>
                {data?.details?.redis?.status ?? 'n/a'}
              </Tag>
              <Text>latency:</Text>
              {latencyTag(data?.details?.redis?.latencyMs)}
              {data?.details?.redis?.queue?.name ? (
                <Tooltip title="Queue wait/active/delayed">
                  <Tag color="blue">
                    {data?.details?.redis?.queue?.name} · w{data?.details?.redis?.queue?.wait ?? 0}
                    /a{data?.details?.redis?.queue?.active ?? 0}/d
                    {data?.details?.redis?.queue?.delayed ?? 0}
                  </Tag>
                </Tooltip>
              ) : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Env">
            <Space size="small" wrap>
              <Text>required:</Text>
              <Tag color="blue">{envSummary.required}</Tag>
              <Text>optional:</Text>
              <Tag>{envSummary.optional}</Tag>
              <Text>missing:</Text>
              <Tag color={envSummary.missing > 0 ? 'red' : 'green'}>{envSummary.missing}</Tag>
              <Text>invalid:</Text>
              <Tag color={envSummary.invalid > 0 ? 'red' : 'green'}>{envSummary.invalid}</Tag>
            </Space>
            {envSummary.missing > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Missing required:</Text>
                <div>
                  {envSummary.names.missing.map((k) => (
                    <Tag key={k} color="red">
                      {k}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            {envSummary.invalid > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Invalid required:</Text>
                <div>
                  {envSummary.names.invalid.map((k) => (
                    <Tag key={k} color="gold">
                      {k}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Card>
  );
}
